// Centralized numeric helpers to avoid floating precision artifacts across the app

export const roundTo = (value: number, decimals: number): number => {
  if (!Number.isFinite(value)) return 0;
  const factor = Math.pow(10, decimals);
  return Math.round((value + Number.EPSILON) * factor) / factor;
};

export const roundMoney = (value: number): number => roundTo(value, 2);

export const clamp = (value: number, min: number, max: number): number => {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
};

// Clean percent input (0-100), keep two decimals max
export const sanitizePercent = (value: number): number => {
  const clamped = clamp(value, 0, 100);
  return roundTo(clamped, 2);
};

// Safe multiplication and addition with rounding at 2 decimals for money
export const mulMoney = (a: number, b: number): number => roundMoney(a * b);
export const addMoney = (a: number, b: number): number => roundMoney(a + b);
export const subMoney = (a: number, b: number): number => roundMoney(a - b);

// Format currency with proper locale formatting
export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('PKR', {
    style: 'currency',
    currency: 'PKR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(roundMoney(value));
};

// Format currency without decimals for outstanding amounts
export const formatCurrencyNoDecimals = (value: number): string => {
  return new Intl.NumberFormat('PKR', {
    style: 'currency',
    currency: 'PKR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(value));
};

// Convert currency to PKR using exchange rate
export const convertToPKR = (amount: number, currency: string, exchangeRate: number): number => {
  if (currency === 'PKR') {
    return amount;
  }
  return roundMoney(amount * exchangeRate);
};

// Format currency conversion display (e.g., "USD 100.00 (PKR 28,000.00)")
export const formatCurrencyWithConversion = (amount: number, currency: string, exchangeRate: number): string => {
  if (currency === 'PKR') {
    return `${currency} ${amount.toFixed(2)}`;
  }
  const pkrAmount = convertToPKR(amount, currency, exchangeRate);
  return `${currency} ${amount.toFixed(2)} (PKR ${pkrAmount.toFixed(2)})`;
};


